import {
  ApiResponse,
  CallAPIReturn,
  NalogRequestData,
  RequestData
} from '../types/types';
import { NALOG_API, PUBLIC_API } from '../constants/config';
import { version } from './version';
import { reportError } from './reportError';

export type ApiCallErrorInfo = {
  message: string;
  status?: number;
  raw?: unknown;
};

type CallOptions<T> = {
  fallback?: T;
  timeoutMs?: number;
  onError?: (info: ApiCallErrorInfo) => void;
  /**
   * Return false to suppress Sentry reporting for an expected failure the
   * caller renders dedicated UI for (e.g. a business-state 4xx). Defaults to
   * reporting every failure. Only consulted for non-2xx responses — genuine
   * network failures and timeouts are always reported.
   */
  shouldReport?: (info: ApiCallErrorInfo) => boolean;
};

const extractErrorMessage = (payload: unknown, status: number): string => {
  const fromBody =
    (payload &&
      typeof payload === 'object' &&
      'message' in (payload as Record<string, unknown>) &&
      typeof (payload as Record<string, unknown>).message === 'string' &&
      (payload as Record<string, unknown>).message) ||
    (typeof payload === 'string' && payload) ||
    `HTTP ${status}`;
  return String(fromBody);
};

// Thrown by query/mutation fns that want React Query to surface a callPublicAPI
// failure via `error`/`isError`. Carries the structured failure info so callers
// can branch on HTTP status or inspect the raw error body.
export class ApiCallError extends Error {
  status?: number;
  raw?: unknown;
  constructor(info: ApiCallErrorInfo) {
    super(info.message);
    this.name = 'ApiCallError';
    this.status = info.status;
    this.raw = info.raw;
  }
}

export const callNalogAPI = async <T = CallAPIReturn>(
  method: RequestInit['method'],
  url: string,
  data?: NalogRequestData,
  options?: CallOptions<T>
): Promise<T> => {
  const { fallback, timeoutMs = 15000, onError } = options ?? {};
  const config: RequestInit = {
    method,
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json'
    },
    body: data ? JSON.stringify(data) : undefined
  };

  const fetchTarget = `${NALOG_API}${url}`;

  // timeout preko AbortController-a
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  config.signal = controller.signal;

  try {
    const res = await fetch(fetchTarget, config);

    // 204 No Content – vrati undefined kao T
    if (res.status === 204) {
      return undefined as unknown as T;
    }

    const ct = res.headers.get('content-type') || '';
    const isJson = ct.includes('application/json');

    // bezbedan parse (nikad ne baca)
    let payload: unknown;
    if (isJson) {
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }
    } else {
      try {
        payload = await res.text();
      } catch {
        payload = '';
      }
    }

    if (!res.ok) {
      const msg = extractErrorMessage(payload, res.status);
      onError?.({ message: String(msg), status: res.status, raw: payload });
      return (fallback as T) ?? (null as unknown as T);
    }

    return payload as T;
  } catch (err) {
    const message =
      err instanceof Error
        ? err.name === 'AbortError'
          ? 'Zahtev je istekao (timeout).'
          : err.message
        : 'Mrežna greška';

    onError?.({ message });
    return (fallback as T) ?? (null as unknown as T);
  } finally {
    clearTimeout(timer);
  }
};

export const callPublicAPI = async <T = CallAPIReturn>(
  method: RequestInit['method'],
  url: string,
  params?: URLSearchParams | null,
  data?: RequestData,
  signal?: AbortSignal,
  additionalHeaders?: Record<string, string>,
  options?: CallOptions<ApiResponse<T>>
): Promise<ApiResponse<T>> => {
  const { fallback, timeoutMs = 15000, onError, shouldReport } = options ?? {};

  const headers: Record<string, string> = {
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
    'X-Version': version,
    ...(additionalHeaders ?? {})
  };

  const controller = new AbortController();
  let didTimeout = false;
  const timer = setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeoutMs);

  if (signal) {
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const config: RequestInit = {
    method,
    credentials: 'include',
    headers,
    signal: controller.signal,
    ...(data && { body: JSON.stringify(data) })
  };

  const query = params ? `?${params.toString()}` : '';
  const fetchTarget = `${PUBLIC_API}${url}${query}`;

  // Failure resolution (declared return type stays ApiResponse<T> for the ~70
  // call sites that read `response.data`):
  //   - `fallback` provided  → return it (caller wants a default on failure)
  //   - `onError` provided    → return null (caller inspects onError + rethrows
  //                             its own ApiCallError; see the migrated consumers)
  //   - neither               → throw ApiCallError, so an unmigrated caller fails
  //                             with a typed error React Query surfaces as
  //                             `isError` instead of a silent `null` it later
  //                             dereferences into a misleading TypeError (which
  //                             would also get double-reported to Sentry — the
  //                             global handler skips ApiCallError, not TypeError).
  const resolveFailure = (info: ApiCallErrorInfo): ApiResponse<T> => {
    if (fallback !== undefined) {
      return fallback as ApiResponse<T>;
    }
    if (onError) {
      return null as unknown as ApiResponse<T>;
    }
    throw new ApiCallError(info);
  };

  try {
    const res = await fetch(fetchTarget, config);

    if (res.status === 204) {
      return undefined as unknown as ApiResponse<T>;
    }

    const ct = res.headers.get('content-type') || '';
    const isJson = ct.includes('application/json');

    // safe parse — never throws to the caller
    let payload: unknown;
    if (isJson) {
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }
    } else {
      try {
        payload = await res.text();
      } catch {
        payload = '';
      }
    }

    if (!res.ok) {
      const info: ApiCallErrorInfo = {
        message: extractErrorMessage(payload, res.status),
        status: res.status,
        raw: payload
      };
      onError?.(info);
      // Make the silent API failure visible in Sentry. No-op in dev / when
      // Sentry is uninitialized, and rate-limited per `op`. Callers render their
      // own UI for an expected business-state failure can opt out via
      // options.shouldReport. The two migrated consumers rethrow this as
      // ApiCallError, so the global query handler skips ApiCallError to avoid a
      // duplicate capture — see queryClientFactory.
      if (shouldReport?.(info) ?? true) {
        reportError(new ApiCallError(info), {
          op: 'callPublicAPI',
          status: res.status,
          payload,
          extras: { url: fetchTarget, method }
        });
      }
      return resolveFailure(info);
    }

    return payload as ApiResponse<T>;
  } catch (err) {
    // resolveFailure may throw ApiCallError from the !res.ok branch above; that
    // is our own deliberate, already-handled (onError + reportError) failure —
    // rethrow it as-is rather than re-processing it as a network error.
    if (err instanceof ApiCallError) {
      throw err;
    }

    const isAbort = err instanceof Error && err.name === 'AbortError';
    const message = isAbort
      ? 'Zahtev je istekao (timeout).'
      : err instanceof Error
        ? err.message
        : 'Mrežna greška';

    const info: ApiCallErrorInfo = { message };
    onError?.(info);
    // Report genuine network failures and our own timeouts; skip consumer- or
    // navigation-triggered cancellations (these also surface as AbortError).
    if (!isAbort || didTimeout) {
      reportError(err, {
        op: 'callPublicAPI',
        extras: {
          url: fetchTarget,
          method,
          kind: didTimeout ? 'timeout' : 'network'
        }
      });
    }
    return resolveFailure(info);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Like {@link callPublicAPI}, but throws an {@link ApiCallError} on any transport
 * failure (non-2xx, network, timeout) instead of returning `null`. Use from
 * non-React-Query code that wraps the call in try/catch, so a failure is an
 * explicit, typed throw rather than a `null` deref. Logical errors delivered as
 * a 2xx envelope (e.g. `statusCode: 403` in the body) are returned as-is.
 */
export const callPublicAPIOrThrow = async <T = CallAPIReturn>(
  method: RequestInit['method'],
  url: string,
  params?: URLSearchParams | null,
  data?: RequestData,
  signal?: AbortSignal,
  additionalHeaders?: Record<string, string>
): Promise<ApiResponse<T>> => {
  let errInfo: ApiCallErrorInfo | null = null;
  const response = await callPublicAPI<T>(
    method,
    url,
    params,
    data,
    signal,
    additionalHeaders,
    { onError: info => (errInfo = info) }
  );
  if (errInfo) {
    throw new ApiCallError(errInfo);
  }
  return response;
};
